import React from 'react';
import { useParams } from 'react-router-dom';
import DeTimeline from '../../components/datamanagement/DeTimeline';
import DeRightSidebar from '../../components/datamanagement/DeRightSidebar';
import { useDataEntry, useCopyDocuments, useCopyDocumentsByParams } from '../../queries/dataEntryQueries';

/**
 * CopyDocumentPage — Display uploaded Copy Documents for a data entry record.
 *
 * Route: /business/copy-document/:id
 * Active Step in Timeline: 1 (COPY DOCUMENT)
 */
const CopyDocumentPage = () => {
  const { id } = useParams();
  const { data: dataEntry, isLoading: isEntryLoading } = useDataEntry(id);
  const { data: copyDocsApi, isLoading: isDocsLoading } = useCopyDocuments(
    dataEntry?.fileName,
    dataEntry?.blNo
  );

  const isLoading = isEntryLoading || (isDocsLoading && !!dataEntry?.fileName);

  // Extract documents list safely (GET /api/data-entry/copy-documents?fileName=...&blNo=...)
  const rawDocs = Array.isArray(copyDocsApi)
    ? copyDocsApi
    : Array.isArray(copyDocsApi?.data)
    ? copyDocsApi.data
    : Array.isArray(dataEntry?.copyDocuments)
    ? dataEntry.copyDocuments
    : [];



  // Normalize document objects
  const documents = rawDocs.map((item, index) => {
    if (typeof item === 'string') {
      return {
        id: index + 1,
        fileName: `Copy_Doc_${index + 1}.pdf`,
        base64: item,
      };
    }
    return {
      id: item?.id || index + 1,
      fileName: item?.fileName || item?.name || `Copy_Doc_${index + 1}.pdf`,
      base64: item?.base64 || item?.url || item?.filePath || item?.document || '',
    };
  });

  // Base64 helper to Blob URL
  const getPdfBlobUrl = (base64Str) => {
    if (!base64Str) return null;
    const cleanBase64 = base64Str.replace(/^data:application\/pdf;base64,/, '');
    try {
      const byteCharacters = atob(cleanBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (e) {
      console.error('Error parsing base64 PDF:', e);
      return null;
    }
  };

  // Action: Open in new tab
  const handleOpenPdf = (base64Str, fileName) => {
    const url = getPdfBlobUrl(base64Str);
    if (url) {
      window.open(url, '_blank');
    } else {
      alert('Unable to preview document.');
    }
  };

  // Action: Download PDF file
  const handleDownloadSinglePdf = (base64Str, fileName) => {
    const url = getPdfBlobUrl(base64Str);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="d-flex flex-column h-100 overflow-hidden overflow-y-auto py-2 py-md-3 px-2 px-md-4">
      <div
        className="d-flex flex-column flex-grow-1 mx-auto w-100"
        style={{ maxWidth: '1280px' }}
      >
        {/* ── Step Progress Timeline — Step 1 active (COPY DOCUMENT) ── */}
        <div className="flex-shrink-0 pb-1">
          <DeTimeline activeStep={1} />
        </div>

        {/* ── Bootstrap 5 two-column layout ── */}
        <div className="row g-3 flex-grow-1 overflow-hidden">
          {/* ── Main Document View (Left Column) ── */}
          <div className="col-12 col-lg-9 d-flex flex-column">
            {isLoading ? (
              <div className="card shadow-sm w-100 p-5 text-center text-muted">
                <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
                Loading copy documents...
              </div>
            ) : documents.length === 0 ? (
              <div className="card shadow-sm w-100 p-5 text-center d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 180px)' }}>
                <div className="mb-3 text-muted">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"/>
                  </svg>
                </div>
                <h5 className="fw-bold text-secondary mb-1">No Copy Documents Found</h5>
                <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                  There are no copy document files available for this record.
                </p>
              </div>
            ) : (
              <div className="w-100 d-flex flex-column" style={{ minHeight: 'calc(100vh - 180px)' }}>
                {/* Header Title */}
                <div className="d-flex justify-content-between align-items-center mb-3 bg-white p-3 border rounded-3 shadow-sm">
                  <div>
                    <h5 className="fw-bold text-uppercase text-primary mb-0">Copy Documents</h5>
                    <small className="text-muted">Total Files: {documents.length}</small>
                  </div>
                  <span className="fw-bold fs-6 text-dark font-monospace">
                    {dataEntry?.fileName || '-'}
                  </span>
                </div>

                {/* Document Cards Grid */}
                <div className="row g-3">
                  {documents.map((doc, idx) => {
                    const blobUrl = getPdfBlobUrl(doc.base64);

                    return (
                      <div key={doc.id || idx} className="col-12 col-md-6 col-xl-4">
                        <div className="card h-100 shadow-sm border rounded-3 overflow-hidden d-flex flex-column">
                          {/* PDF Preview Area */}
                          <div
                            className="bg-light d-flex align-items-center justify-content-center overflow-hidden position-relative"
                            style={{ height: '160px', cursor: 'pointer' }}
                            onClick={() => handleOpenPdf(doc.base64, doc.fileName)}
                          >
                            {blobUrl ? (
                              <iframe
                                title={doc.fileName}
                                src={`${blobUrl}#toolbar=0&navpanes=0`}
                                className="w-100 h-100 border-0 pointer-events-none"
                                scrolling="no"
                              />
                            ) : (
                              <div className="text-center text-muted p-3">
                                <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="currentColor" viewBox="0 0 16 16">
                                  <path d="M4 0h5.293A1 1 0 0 1 10 .293L13.707 4a1 1 0 0 1 .293.707V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2"/>
                                </svg>
                                <div className="mt-1 small">PDF File</div>
                              </div>
                            )}

                            {/* Hover overlay icon */}
                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-10 opacity-0 hover-opacity-100 transition">
                              <span className="btn btn-sm btn-light fw-bold shadow-sm">🔍 View PDF</span>
                            </div>
                          </div>

                          {/* Card Footer Bar */}
                          <div className="gtss-bg-primary text-white p-2.5 d-flex align-items-center justify-content-between mt-auto">
                            <span
                              className="text-truncate fw-semibold text-white me-2"
                              style={{ fontSize: '12px' }}
                              title={doc.fileName}
                            >
                              📄 {doc.fileName}
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-light text-primary fw-bold p-1 px-2 flex-shrink-0"
                              style={{ fontSize: '11px' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadSinglePdf(doc.base64, doc.fileName);
                              }}
                              title="Download PDF"
                            >
                              📥
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Right Sidebar Column ── */}
          <div className="col-12 col-lg-3">
            <DeRightSidebar id={id} activeStep={1} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopyDocumentPage;
